import { type Control, Controller } from 'react-hook-form'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardCheckbox } from '@/components/ui/CardCheckbox'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phoneInput'
import { INCOMPLETE_PHONE_MESSAGE, isCompletePhone } from '@/utils/phone'
import { ReporterRelationship } from '../../../../models/claim'
import type { Step1FormValues } from './claimFormValues'

const RELATIONSHIPS: { key: ReporterRelationship; label: string }[] = [
  { key: ReporterRelationship.POLICYHOLDER_OWNER, label: 'Policyholder / Business Owner' },
  { key: ReporterRelationship.DRIVER, label: 'Driver' },
  { key: ReporterRelationship.PASSENGER, label: 'Passenger' },
  { key: ReporterRelationship.REPORTER_ONLY, label: 'Reporter Only' },
  { key: ReporterRelationship.FLEET_MANAGER, label: 'Fleet Manager / Safety Coordinator' },
]

export function ClaimReporterSection({ control }: { control: Control<Step1FormValues> }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Who is reporting this claim?</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Full name is required' }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-neutral-700" htmlFor="reporter-name">
                  Full name
                </label>
                <Input
                  id="reporter-name"
                  placeholder="e.g. Jane Smith"
                  aria-invalid={fieldState.error ? true : undefined}
                  {...field}
                />
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />
          <Controller
            name="phone"
            control={control}
            rules={{
              required: 'Phone number is required',
              validate: (value) => isCompletePhone(value) || INCOMPLETE_PHONE_MESSAGE,
            }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-neutral-700" htmlFor="reporter-phone">
                  Phone number
                </label>
                <PhoneInput
                  id="reporter-phone"
                  error={!!fieldState.error}
                  value={field.value}
                  onChange={field.onChange}
                />
                <FieldError message={fieldState.error?.message} />
              </div>
            )}
          />
        </div>

        <Controller
          name="relationships"
          control={control}
          rules={{ validate: (v) => v.length > 0 || 'Select at least one' }}
          render={({ field, fieldState }) => (
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700">Relationship to incident</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {RELATIONSHIPS.map(({ key, label }) => (
                  <CardCheckbox
                    key={key}
                    isChecked={field.value.includes(key)}
                    onChange={(next) =>
                      field.onChange(next ? [...field.value, key] : field.value.filter((r) => r !== key))
                    }
                  >
                    {label}
                  </CardCheckbox>
                ))}
              </div>
              <FieldError message={fieldState.error?.message} />
            </div>
          )}
        />
      </CardContent>
    </Card>
  )
}
